/**
 * HeroSection.ts
 *
 * Secao hero da landing page do SwimSentry.
 * Design focado em conversao com CTAs claros e video demo.
 *
 * Principios UX aplicados:
 * - Headline em <1s para entender proposta
 * - CTAs com tamanho minimo de 44px (touch-friendly)
 * - Trust badges para credibilidade imediata
 */

export interface HeroConfig {
  headline: string;
  subheadline: string;
  primaryCta: {
    text: string;
    href: string;
  };
  secondaryCta: {
    text: string;
    href: string;
  };
  videoSrc?: string;
  videoPoster?: string;
}

const DEFAULT_CONFIG: HeroConfig = {
  headline: 'AI Pool Safety That Watches While You Can\'t',
  subheadline: 'Detects drowning signs in 2.5 seconds using your phone camera. No app. No hardware. Just peace of mind.',
  primaryCta: {
    text: 'Try Free for 2 Min',
    href: '/app'
  },
  secondaryCta: {
    text: 'See How It Works',
    href: '#how-it-works'
  }
};

export class HeroSection {
  private container: HTMLElement;
  private config: HeroConfig;

  constructor(container: HTMLElement, config: Partial<HeroConfig> = {}) {
    this.container = container;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  render(): void {
    this.container.innerHTML = `
      <section class="hero-section landing-section" id="hero">
        <div class="hero-content">
          <!-- Brand -->
          <div class="hero-brand">
            <h1 class="hero-logo">SwimSentry</h1>
            <p class="hero-tagline">Watch Over What Matters Most</p>
          </div>

          <!-- Video Demo -->
          <div class="hero-video-container">
            ${this.renderVideo()}
          </div>

          <!-- Headlines -->
          <h2 class="hero-headline">${this.config.headline}</h2>
          <p class="hero-subheadline">${this.config.subheadline}</p>

          <!-- CTAs -->
          <div class="hero-ctas">
            <a href="${this.config.primaryCta.href}" class="btn-primary">
              <span>&#127909;</span>
              <span>${this.config.primaryCta.text}</span>
            </a>
            <a href="${this.config.secondaryCta.href}" class="btn-secondary">
              <span>${this.config.secondaryCta.text}</span>
            </a>
          </div>

          <!-- Trust Badges -->
          <div class="hero-trust">
            <div class="trust-badge">
              <span class="trust-badge-check">&#10003;</span>
              <span>No app download</span>
            </div>
            <div class="trust-badge">
              <span class="trust-badge-check">&#10003;</span>
              <span>No hardware needed</span>
            </div>
            <div class="trust-badge">
              <span class="trust-badge-check">&#10003;</span>
              <span>Works in your browser</span>
            </div>
          </div>
        </div>
      </section>
    `;

    this.attachEventListeners();
  }

  private renderVideo(): string {
    if (this.config.videoSrc) {
      return `
        <video
          class="hero-video"
          autoplay
          muted
          loop
          playsinline
          poster="${this.config.videoPoster || ''}"
        >
          <source src="${this.config.videoSrc}" type="video/mp4">
          Your browser does not support the video tag.
        </video>
      `;
    }

    // Placeholder quando nao tem video
    return `
      <div class="hero-video-placeholder">
        <span class="hero-video-placeholder-icon">&#127909;</span>
        <span>Demo video coming soon</span>
      </div>
    `;
  }

  private attachEventListeners(): void {
    // Smooth scroll para links internos
    const links = this.container.querySelectorAll('a[href^="#"]');
    links.forEach(link => {
      link.addEventListener('click', (e) => {
        const href = link.getAttribute('href');
        if (href && href.startsWith('#')) {
          e.preventDefault();
          const target = document.querySelector(href);
          if (target) {
            target.scrollIntoView({ behavior: 'smooth' });
          }
        }
      });
    });
  }

  /**
   * Atualiza o headline dinamicamente (A/B testing)
   */
  setHeadline(headline: string): void {
    const el = this.container.querySelector('.hero-headline');
    if (el) {
      el.textContent = headline;
    }
  }

  /**
   * Atualiza video source
   */
  setVideo(src: string, poster?: string): void {
    const container = this.container.querySelector('.hero-video-container');
    if (container) {
      this.config.videoSrc = src;
      this.config.videoPoster = poster;
      container.innerHTML = this.renderVideo();
    }
  }
}

export default HeroSection;
